// src/pages/farmer/MachineryApplyForm.js
// ✅ FINAL READY-TO-PASTE (Premium + Redirect to Member Dashboard after submit)
// ✅ No business logic removed: same Firestore addDoc fields kept
// ✅ Added: useNavigate + redirect after successful submit
// ✅ Added: Submit button loading UX + auto-scroll top
// ✅ (Optional existing) generateQuotationPDF kept, still commented as you wanted

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom"; // ✅ ADD useNavigate
import { db, auth } from "../../firebaseConfig"; // ✅ ADD auth

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import jsPDF from "jspdf";

const cx = (...xs) => xs.filter(Boolean).join(" ");

// helper safe string
const s = (v) => String(v || "").trim();

// ✅ FIX START: vendor safe company name resolver (company_name missing issue)
const getVendorCompanyName = (v) => {
  return (
    v?.company_name ||
    v?.companyName ||
    v?.firmName ||
    v?.firm_name ||
    v?.profile?.companyName ||
    v?.profile?.company_name ||
    v?.profile?.firmName ||
    v?.name ||
    v?.profile?.name ||
    ""
  );
};
// ✅ FIX END

export default function MachineryApplyForm() {
  const location = useLocation();
  const navigate = useNavigate(); // ✅ ADD
  const params = new URLSearchParams(location.search);

  const memberId = params.get("memberId") || "";
  const vendorId = params.get("vendorId") || "";
  const productId = params.get("productId") || "";

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [product, setProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // member
        let memberData = null;
        if (memberId) {
          const mq = query(collection(db, "members"), where("member_id", "==", memberId));
          const msnap = await getDocs(mq);
          if (!msnap.empty) {
            const d = msnap.docs[0];
            memberData = { id: d.id, ...d.data() };
          }
        }

        // vendor
        let vendorData = null;
        if (vendorId) {
          const vsnap = await getDoc(doc(db, "farmer_vendors", vendorId));
          if (vsnap.exists()) vendorData = { id: vsnap.id, ...vsnap.data() };
        }

        // product
        let productData = null;
        if (vendorId && productId) {
          const psnap = await getDoc(doc(db, "farmer_vendors", vendorId, "products", productId));
          if (psnap.exists()) productData = { id: psnap.id, ...psnap.data() };
        }

        setMember(memberData);
        setVendor(vendorData);
        setProduct(productData);
      } catch (err) {
        console.error("MachineryApplyForm load error:", err);
        alert("Error loading application data: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [memberId, vendorId, productId]);

  // kept (not removed)
  const generateQuotationPDF = (memberObj, vendorObj, productObj, appId) => {
    const docPdf = new jsPDF("p", "mm", "a4");

    const companyBrand = s(productObj?.companyBrand || productObj?.brand);
    const branchLabel = s(productObj?.branchLabel);

    docPdf.setFontSize(16);
    docPdf.text(
      getVendorCompanyName(vendorObj) || vendorObj?.company_name || "Vendor Company",
      20,
      20
    );

    docPdf.setFontSize(10);
    const vendorAddrLine = [
      s(vendorObj?.address),
      s(vendorObj?.taluka) ? `Taluka: ${s(vendorObj?.taluka)}` : "",
      s(vendorObj?.district) ? `Dist: ${s(vendorObj?.district)}` : "",
      s(vendorObj?.state) ? `State: ${s(vendorObj?.state)}` : "",
      branchLabel ? `Branch: ${branchLabel}` : "",
    ]
      .filter(Boolean)
      .join(" | ");

    docPdf.text(vendorAddrLine, 20, 26, { maxWidth: 170 });

    docPdf.setFontSize(14);
    docPdf.text("QUOTATION", 105, 40, { align: "center" });

    docPdf.setFontSize(10);
    docPdf.text(`To : ${memberObj?.name || ""}`, 20, 50);

    docPdf.text(
      `Village : ${memberObj?.city || ""}, Taluka : ${memberObj?.taluka || ""}, Dist : ${memberObj?.district || ""}`,
      20,
      56
    );

    docPdf.text(`Date : ${new Date().toLocaleDateString()}`, 150, 50);
    if (memberId) docPdf.text(`Ref : ${memberId}`, 150, 56);

    let y = 70;
    docPdf.setFontSize(10);
    docPdf.text("Sr.", 20, y);
    docPdf.text("Particulars", 30, y);
    docPdf.text("Amount (₹)", 160, y, { align: "right" });

    y += 6;
    docPdf.text("1", 20, y);

    const particulars =
      `${companyBrand} ${s(productObj?.modelName)} ${s(productObj?.hpRating)}`.trim() ||
      productObj?.title ||
      productObj?.name ||
      "Tractor / Machine";

    docPdf.text(particulars, 30, y, { maxWidth: 110 });

    const amount =
      productObj?.onRoadPriceApprox ||
      productObj?.priceExShowroom ||
      productObj?.basePrice ||
      0;

    docPdf.text(String(amount), 160, y, { align: "right" });

    y += 12;
    docPdf.setFontSize(11);
    docPdf.text("TOTAL", 120, y);
    docPdf.text(String(amount), 160, y, { align: "right" });

    y += 10;
    docPdf.setFontSize(9);
    docPdf.text(
      "Note: This is a system-generated quotation request slip. Final quotation will be issued by the vendor on letterhead.",
      20,
      y,
      { maxWidth: 170 }
    );

    y += 10;
    docPdf.text("Customer's Signature", 20, y);
    docPdf.text(
      "For, " + (getVendorCompanyName(vendorObj) || vendorObj?.company_name || "Vendor"),
      140,
      y
    );

    docPdf.save(
      `Quotation_${vendorObj?.company_name || "Vendor"}_${memberObj?.name || "Farmer"}_${appId || ""}.pdf`
    );
  };

  const handleSubmit = async () => {
    if (saving) return;
    if (!member || !vendor || !product) {
      alert("Data not loaded properly. Please try again.");
      return;
    }

    try {
      setSaving(true);

      const machGroup = s(product.machGroup);
      const companyBrand = s(product.companyBrand || product.brand);
      const branchLabel = s(product.branchLabel);

      const refCol = collection(db, "machinery_applications");

      const farmerVillage = member.village || member.city || "";
      const vendorCompany = getVendorCompanyName(vendor) || vendor.company_name || "";

      const docRef = await addDoc(refCol, {
        // farmer
        memberId: member.member_id || "",
        memberDocId: member.id,
        memberAuthUid: auth.currentUser?.uid || "",   // ✅ ADD THIS
        farmerName: member.name || "",
        farmerMobile: member.mobile || "",
        farmerState: member.state || "",
        farmerDistrict: member.district || "",
        farmerTaluka: member.taluka || "",
        farmerVillage: farmerVillage || "",

        // vendor
        vendorId: vendor.id,
        vendorName: vendorCompany || "",
        vendorTaluka: vendor.taluka || "",
        vendorDistrict: vendor.district || "",
        vendorState: vendor.state || "",

        // product
        productId: product.id,
        productTitle: product.title || product.name || "",
        machineType: product.machineType || "",

        // connected
        machGroup: machGroup || "",
        companyBrand: companyBrand || "",
        branchLabel: branchLabel || "",

        // legacy
        brand: product.brand || companyBrand || "",
        modelName: product.modelName || "",
        hpRating: product.hpRating || "",
        onRoadPriceApprox: product.onRoadPriceApprox || product.priceExShowroom || 0,

        status: "submitted",
        createdAt: serverTimestamp(),
      });

      // ✅ You kept this off (correct)
      // generateQuotationPDF(member, vendor, product, docRef.id);

      alert(
        "✅ Application submitted successfully.\nVendor will generate final quotation on letterhead.\nYou can download the final quotation from your portal after vendor uploads it."
      );

      // ✅ Redirect back to Member Dashboard
      navigate("/member-login?panel=requests", { replace: true });

    } catch (err) {
      console.error("Machinery application error:", err);
      alert("Error submitting application: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-600">
        Loading application details…
      </div>
    );
  }

  if (!member || !vendor || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-rose-600">
        Unable to load farmer / vendor / product details. Please go back and try again.
      </div>
    );
  }

  const companyBrand = s(product.companyBrand || product.brand);
  const branchLabel = s(product.branchLabel);
  const vendorCompany = getVendorCompanyName(vendor) || vendor.company_name || "-";

  return (
    <div className="min-h-screen bg-[radial-gradient(1200px_520px_at_50%_-140px,#dbeafe_0%,#f1f5f9_45%,#ffffff_100%)] py-10">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-5 bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-950 text-white">
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-white/80">
              📝 Machinery Application – Quotation
            </div>
            <h1 className="mt-2 text-[18px] md:text-[22px] font-extrabold tracking-tight">
              Confirm Details &amp; Submit Request
            </h1>
            <p className="mt-1 text-[12px] text-white/85 max-w-2xl">
              Verify your details and selected machine. After submission, your request is recorded and the vendor will issue the final quotation on letterhead.
            </p>
          </div>
          <div className="px-6 py-4 bg-white flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-[12px] text-slate-700">
              Member ID: <b className="text-slate-900">{member.member_id || "-"}</b> • Vendor:{" "}
              <b className="text-slate-900">{vendorCompany}</b>
              {branchLabel ? (
                <span className="text-slate-600"> • Branch: <b className="text-slate-900">{branchLabel}</b></span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[12px] font-extrabold text-slate-800"
            >
              ⬅ Back
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Farmer */}
          <div className="lg:col-span-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-5 text-[12px]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[12px] font-extrabold text-slate-900">👨‍🌾 Farmer Details</div>
              <span className="px-2 py-1 rounded-full border text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border-emerald-200">
                Verified
              </span>
            </div>
            <div className="text-slate-700 space-y-2">
              <div><span className="font-extrabold text-slate-900">Name:</span> {member.name}</div>
              <div><span className="font-extrabold text-slate-900">Mobile:</span> {member.mobile || "-"}</div>
              <div><span className="font-extrabold text-slate-900">Membership ID:</span> {member.member_id}</div>
              <div>
                <span className="font-extrabold text-slate-900">Area:</span>{" "}
                {member.village || member.city || "-"}
                {member.taluka ? `, ${member.taluka}` : ""}
                {member.district ? `, ${member.district}` : ""}
                {member.state ? ` (${member.state})` : ""}
              </div>
            </div>
          </div>

          {/* Vendor */}
          <div className="lg:col-span-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-5 text-[12px]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[12px] font-extrabold text-slate-900">🏢 Vendor Details</div>
              <span className="px-2 py-1 rounded-full border text-[10px] font-extrabold bg-indigo-50 text-indigo-700 border-indigo-200">
                Active
              </span>
            </div>
            <div className="text-slate-700 space-y-2">
              <div><span className="font-extrabold text-slate-900">Company:</span> {vendorCompany}</div>
              <div><span className="font-extrabold text-slate-900">Contact Person:</span> {vendor.contact_name || "-"}</div>
              <div><span className="font-extrabold text-slate-900">Mobile:</span> {vendor.mobile || "-"}</div>
              <div>
                <span className="font-extrabold text-slate-900">Area:</span>{" "}
                {vendor.taluka ? `${vendor.taluka}, ` : ""}{vendor.district ? `${vendor.district} ` : ""}{vendor.state ? `(${vendor.state})` : ""}
              </div>
              {branchLabel ? (
                <div><span className="font-extrabold text-slate-900">Branch:</span> {branchLabel}</div>
              ) : null}
            </div>
          </div>

          {/* Machine */}
          <div className="lg:col-span-12 bg-white border border-slate-200 rounded-2xl shadow-sm p-5 text-[12px]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[12px] font-extrabold text-slate-900">🚜 Machine / Tractor Details</div>
              <span className="px-2 py-1 rounded-full border text-[10px] font-extrabold bg-amber-50 text-amber-800 border-amber-200">
                Review
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-slate-700">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Type</div>
                <div className="mt-1 font-extrabold text-slate-900">{product.machineType || "Tractor / Machine"}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Model</div>
                <div className="mt-1 font-extrabold text-slate-900">
                  {companyBrand} {product.modelName || ""} {product.hpRating ? `(${product.hpRating})` : ""}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Group</div>
                <div className="mt-1 font-extrabold text-slate-900">{product.machGroup || "-"}</div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 md:col-span-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Usage</div>
                    <div className="mt-1 font-extrabold text-slate-900">{product.usageType || "Purchase"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">Ex-showroom</div>
                    <div className="mt-1 font-extrabold text-slate-900">{product.priceExShowroom ? `₹${product.priceExShowroom}` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-extrabold text-slate-500">On-road approx</div>
                    <div className="mt-1 font-extrabold text-slate-900">{product.onRoadPriceApprox ? `₹${product.onRoadPriceApprox}` : "—"}</div>
                  </div>
                </div>

                <div className="mt-4 text-[11px] text-slate-600">
                  ✅ After submit, vendor will generate final quotation on letterhead. You can download it from Member Dashboard → My Requests.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={saving}
            className={cx(
              "px-5 py-2.5 rounded-xl border text-[12px] font-extrabold",
              "border-slate-200 bg-white hover:bg-slate-50 text-slate-800",
              saving ? "opacity-60 cursor-not-allowed" : ""
            )}
          >
            ⬅ Back
          </button>

          <button
            type="button"
            disabled={saving}
            onClick={handleSubmit}
            className={cx(
              "px-7 py-2.5 rounded-xl text-white text-[12px] font-extrabold shadow",
              "bg-emerald-600 hover:bg-emerald-700",
              saving ? "opacity-60 cursor-not-allowed" : ""
            )}
          >
            {saving ? "Submitting…" : "✅ Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}
